import { type Token } from '@stripe/stripe-js'
import { apiPost } from '@/features/shared/utils/dataFetching'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type RedeemCreditPayload,
  type RedeemCreditResponse,
} from '@/features/purchase/services/GiftCard/types'
import { type SubscriptionResponse } from '@/features/account/services/MembershipPlan/types'
import { UserQuery } from '@/features/account/services/User/constants'
import { useUserId } from '@/features/account/services/User/hooks'
import { membershipPlanQueryOptions } from '@/features/account/services/MembershipPlan/queries'

export const RedeemCreditKey = 'Redeem Credit'

export const useMutationRedeemCredit = () => {
  return useMutation({
    mutationKey: [RedeemCreditKey],
    mutationFn: ({ token, creditAmount }: { token: Token; creditAmount: number }) => {
      const payload: RedeemCreditPayload = {
        card_token: token.id,
        credit_amount: creditAmount,
        send_email: 'true',
        platform_source: 'web',
      }

      return apiPost<RedeemCreditResponse>({
        config: {
          url: 'api/v1/customers/purchase_credit.json',

          data: payload,
        },
        fetcherName: 'useMutationRedeemCredit',
      })
    },
  })
}

export const useMutationRedeemMembership = () => {
  const queryClient = useQueryClient()
  const userId = useUserId() ?? 0

  return useMutation({
    mutationFn: (planId: string) => {
      const payload = {
        plan_id: planId,
        platform_source: 'web',
      }
      return apiPost<SubscriptionResponse | null>({
        config: {
          url: `/v1/customers/${userId}/subscriptions`,
          data: payload,
        },
        options: {
          ignoredMessages: ['Payment card was declined.'],
        },
        fetcherName: 'useMutationRedeemMembership',
      })
    },
    onSuccess: (subscription) => {
      queryClient.setQueryData(membershipPlanQueryOptions(userId).queryKey, subscription)
      // We want to fetch user data on membership change
      queryClient.invalidateQueries({ queryKey: [UserQuery] })
    },
  })
}
